import { ImagePickerOptions, launchCameraAsync, launchImageLibraryAsync } from "expo-image-picker";
import React, { useCallback, useMemo, useState } from "react";
import { Linking, Platform, Pressable, View } from "react-native";
import Animated, { WithTimingConfig, useAnimatedStyle, withTiming } from "react-native-reanimated";
import { useI18nMessage } from "@lookiero/i18n-react";
import { useLogger } from "@lookiero/sty-psp-logging";
import { NotificationLevel, useCreateToastNotification } from "@lookiero/sty-psp-notifications";
import {
  QuestionItem,
  QuestionType,
  useAnswerForId,
  useAnswersValidation,
  useUploadImage,
} from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { AspectRatioView, Icon, Text } from "@lookiero/sty-psp-ui";
import { useUploadImage as useUploadImageCommand } from "../../../../../../../infrastructure/domain/image/react/useUploadImage";
import { MESSAGING_CONTEXT_ID } from "../../../../../../delivery/baseBootstrap";
import { useTrackImageUploaded } from "../../../../../../tracking/useTrackImageUploaded";
import { useTrackUploadPhotoChooseLibraryOptionCanceled } from "../../../../../../tracking/useTrackUploadPhotoChooseLibraryOptionCanceled";
import { useTrackUploadPhotoChooseLibraryOptionSelected } from "../../../../../../tracking/useTrackUploadPhotoChooseLibraryOptionSelected";
import { useTrackUploadPhotoError } from "../../../../../../tracking/useTrackUploadPhotoError";
import { useTrackUploadPhotoOptionsModalClose } from "../../../../../../tracking/useTrackUploadPhotoOptionsModalClose";
import { useTrackUploadPhotoOptionsModalView } from "../../../../../../tracking/useTrackUploadPhotoOptionsModalView";
import { useTrackUploadPhotoTakePhotoOptionCanceled } from "../../../../../../tracking/useTrackUploadPhotoTakePhotoOptionCanceled";
import { useTrackUploadPhotoTakePhotoOptionSelected } from "../../../../../../tracking/useTrackUploadPhotoTakePhotoOptionSelected";
import { useTrackUploadPhotoUploadIconSelected } from "../../../../../../tracking/useTrackUploadPhotoUploadIconSelected";
import { useFecthImage } from "../../../../../hooks/useFetchImage";
import { useStaticInfo } from "../../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages, PREFIX } from "../../../../../i18n/i18n";
import { style as hostImageUploadQuestionItemStyle } from "./HostImageUploadQuestionItem.style";
import { ImagePickerModal } from "./components/imagePickerModal/ImagePickerModal";
import { requestCameraPermissions, requestMediaLibraryPermissions } from "./permissions";

const IMAGE_LIBRARY_OPTIONS: ImagePickerOptions = {
  mediaTypes: "images",
  selectionLimit: 1,
  quality: 1,
};

const CAMERA_OPTIONS: ImagePickerOptions = {
  mediaTypes: "images",
  quality: 1,
};

const IMAGE_ANIMATIONS_OPTIONS: WithTimingConfig = {
  duration: 500,
};

const HostImageUploadQuestionItem: QuestionItem<QuestionType.HOST_IMAGE_UPLOAD> = ({ question }) => {
  const {
    customer: { country, customerId, segment },
    tradename,
    androidInAppBrowser,
  } = useStaticInfo();
  const logger = useLogger();

  const androidInAppBrowserText = useI18nMessage({
    domain: DOMAIN,
    prefix: PREFIX,
    id: I18nMessages.ANDROID_IN_APP_BROWSER_UPLOAD_HELPER_TEXT,
  });

  const style = useMemo(() => hostImageUploadQuestionItemStyle(), []);

  const [imageLoading, setImageLoading] = useState(false);

  const trackUploadPhotoUploadIconSelected = useTrackUploadPhotoUploadIconSelected({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackUploadPhotoOptionsModalView = useTrackUploadPhotoOptionsModalView({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackUploadPhotoTakePhotoOptionSelected = useTrackUploadPhotoTakePhotoOptionSelected({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackUploadPhotoChooseLibraryOptionSelected = useTrackUploadPhotoChooseLibraryOptionSelected({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackUploadPhotoError = useTrackUploadPhotoError({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackImageUploaded = useTrackImageUploaded({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackUploadPhotoOptionsModalClose = useTrackUploadPhotoOptionsModalClose({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackUploadPhotoChooseLibraryOptionCanceled = useTrackUploadPhotoChooseLibraryOptionCanceled({
    country,
    customerId,
    segment,
    tradename,
  });
  const trackUploadPhotoTakePhotoOptionCanceled = useTrackUploadPhotoTakePhotoOptionCanceled({
    country,
    customerId,
    segment,
    tradename,
  });

  const [imagePickerModalVisible, setImagePickerModalVisible] = useState(false);
  const handleOnImagePickerModalClose = useCallback(() => setImagePickerModalVisible(false), []);

  const [createNotification] = useCreateToastNotification({ contextId: MESSAGING_CONTEXT_ID, logger });
  const handleOnError = useCallback(
    (error?: string) => {
      trackUploadPhotoError();
      createNotification({
        level: NotificationLevel.ERROR,
        bodyI18nKey: error || I18nMessages.UPLOAD_IMAGE_TOAST_GENERIC_ERROR,
      });
    },
    [createNotification, trackUploadPhotoError],
  );

  const [upload, uploadStatus] = useUploadImageCommand({
    logger,
  });
  const uploadImage = useUploadImage({
    onStart: handleOnImagePickerModalClose,
    onSuccess: async ({ id, image }) => {
      const uploaded = await upload({
        id,
        image,
        customerId,
        context: question.metadata.context,
      });

      if (!uploaded) {
        return;
      }

      trackImageUploaded();

      onChange({
        questionId: question.id,
        answer: id,
      });

      validateMaxAnswers();
    },
    onError: handleOnError,
  });

  const { answer, onChange } = useAnswerForId({ id: question.id });
  const selectedAnswer = answer && answer[0];
  const [image, imageStatus] = useFecthImage({
    endpoint: `/view-style-profile-image-by-id/${selectedAnswer}`,
    enabled: Boolean(selectedAnswer),
  });

  const { validateMaxAnswers } = useAnswersValidation();

  const handleOnTakePhoto = useCallback(async () => {
    trackUploadPhotoTakePhotoOptionSelected();

    const granted = await requestCameraPermissions();

    if (granted) {
      try {
        const response = await launchCameraAsync(CAMERA_OPTIONS);

        if (response.canceled) {
          trackUploadPhotoTakePhotoOptionCanceled();
          return;
        }

        const image = response.assets?.[0];

        if (image && image.uri) {
          await uploadImage({ uri: image.uri });
        }
      } catch (_error) {
        handleOnError();
      }
    }
  }, [handleOnError, trackUploadPhotoTakePhotoOptionCanceled, trackUploadPhotoTakePhotoOptionSelected, uploadImage]);
  const handleOnChooseFromLibrary = useCallback(async () => {
    trackUploadPhotoChooseLibraryOptionSelected();

    const granted = await requestMediaLibraryPermissions();

    if (granted) {
      try {
        const response = await launchImageLibraryAsync(IMAGE_LIBRARY_OPTIONS);

        if (response.canceled) {
          trackUploadPhotoChooseLibraryOptionCanceled();
          return;
        }

        const image = response.assets?.[0];

        if (image && image.uri) {
          await uploadImage({ uri: image.uri });
        }
      } catch (_error) {
        handleOnError();
      }
    }
  }, [
    handleOnError,
    trackUploadPhotoChooseLibraryOptionCanceled,
    trackUploadPhotoChooseLibraryOptionSelected,
    uploadImage,
  ]);

  const handleOnDismiss = useCallback(() => {
    trackUploadPhotoOptionsModalClose();
    handleOnImagePickerModalClose();
  }, [handleOnImagePickerModalClose, trackUploadPhotoOptionsModalClose]);

  const handleOnPress = useCallback(async () => {
    trackUploadPhotoUploadIconSelected();

    if (Platform.OS !== "web") {
      setImagePickerModalVisible(true);
      trackUploadPhotoOptionsModalView();
    } else {
      await handleOnChooseFromLibrary();
    }
  }, [handleOnChooseFromLibrary, trackUploadPhotoOptionsModalView, trackUploadPhotoUploadIconSelected]);

  const handleOpenExternalBrowser = useCallback(async () => {
    if (typeof window !== "undefined" && window.location) {
      const currentUrl = window.location.href;

      // For Facebook in-app browser, we need to use a special intent URL
      // that forces opening in external browser
      const intentUrl = `intent://${currentUrl.replace(/^https?:\/\//, "")}#Intent;scheme=https;end`;

      try {
        // Try to open with intent URL first (works on Android Facebook webview)
        await Linking.openURL(intentUrl);
      } catch {
        // Fallback to regular URL if intent doesn't work
        try {
          await Linking.openURL(currentUrl);
        } catch (_error) {
          // If both fail, try opening in a new window (web fallback)
          if (typeof window !== "undefined") {
            window.open(currentUrl, "_blank");
          }
        }
      }
    }
  }, []);

  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: withTiming(imageLoading ? 0 : 1, IMAGE_ANIMATIONS_OPTIONS),
    }),
    [imageLoading],
  );
  const handleOnLoadStart = useCallback(() => setImageLoading(true), []);
  const handleOnLoadEnd = useCallback(() => setImageLoading(false), []);

  const loading = uploadStatus === "loading" || imageStatus === "loading" || imageLoading;

  return (
    <View style={androidInAppBrowser && style.androidInAppBrowserContainer}>
      <ImagePickerModal
        disabled={loading}
        testID={`image-picker-${question.id}`}
        visible={imagePickerModalVisible}
        onChooseFromLibrary={handleOnChooseFromLibrary}
        onDismiss={handleOnDismiss}
        onTakePhoto={handleOnTakePhoto}
      />

      <AspectRatioView aspectRatio={5 / 4}>
        <Pressable
          accessibilityLabel="host-image-upload-question-item"
          disabled={loading}
          style={style.container}
          testID="host-image-upload-question-item"
          accessible
          onPress={handleOnPress}
        >
          {selectedAnswer && uploadStatus !== "loading" && imageStatus !== "loading" ? (
            <Animated.Image
              resizeMode="cover"
              source={{ uri: image }}
              testID={question.id}
              style={[
                // @ts-ignore
                style.image,
                animatedStyle,
              ]}
              onLoadEnd={handleOnLoadEnd}
              onLoadStart={handleOnLoadStart}
            />
          ) : (
            <Icon name={loading ? "hourglass" : "expand"} style={style.icon} />
          )}
        </Pressable>
      </AspectRatioView>

      {androidInAppBrowser && (
        <Pressable style={style.androidInAppBrowserPressable} onPress={handleOpenExternalBrowser}>
          <Text level={3} style={style.androidInAppBrowserPressableText} variant="action">
            {androidInAppBrowserText}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

export { HostImageUploadQuestionItem };
